import { useEffect, useState } from 'react';
import { Modal, Form, Select, DatePicker, InputNumber, Button, Row, Col, Input, Typography, Tag, Segmented, message } from '../../ui';
import { Star } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';
import { inr2 } from '../../lib/format';
import { formatIstDate } from '../../lib/time';
import { customerOption, CUSTOMER_SELECT_PROPS } from '../../components/CustomerOption';
import { useRetailerSearch } from '../../hooks/useRetailerSearch';

interface Props {
  open: boolean;
  onClose: () => void;
  /** If provided, pre-fills the retailer. */
  retailerId?: string;
}

const MODES = ['CASH', 'CHEQUE', 'UPI', 'NEFT', 'RTGS', 'IMPS', 'CARD', 'OTHER'];
const inr = (n: any) => `₹${Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
/** Plain-text bank-account label — used for SEARCH matching (Select optionFilterProp) + non-JSX display. */
export const bankAcctLabel = (b: any) =>
  `${b?.label || b?.bankName || 'Bank'}${b?.last4 ? ` ··${b.last4}` : ''}${b?.isPrimary ? ' Primary' : ''}`;

/** The bank-account name + last4 as a plain string (no Primary/Inactive suffix). */
const bankAcctName = (b: any) => `${b?.bankName || b?.label || 'Bank'}${b?.last4 ? ` ··${b.last4}` : ''}`;

/**
 * A Select OPTION for a bank account — the label is rich (name + a green "Primary" chip, matching the
 * Bank Details page), and `searchLabel` is the plain text so type-to-search still works.
 */
export const bankAcctOption = (b: any) => ({
  value: String(b.id),
  searchLabel: bankAcctLabel(b),
  // The name TRUNCATES and the Primary chip never shrinks, so in a narrow picker (e.g. the assign-to-bank
  // popover) the chip stays fully readable instead of being clipped off the edge.
  label: (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate">{bankAcctName(b)}</span>
      {b?.isPrimary && <Tag tone="green" icon={<Star size={14} />} className="shrink-0" style={{ margin: 0 }}>Primary</Tag>}
    </span>
  ),
});

/**
 * Staff-direct payment record (auto-approved — posts the ledger immediately). Order-based: optionally
 * link the payment to one of the retailer's orders to reduce that order's due (invoice allocation is
 * retired). Leave the order blank for an on-account / advance payment.
 */
export function PaymentFormModal({ open, onClose, retailerId }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const [retailer, setRetailer] = useState<string | undefined>(retailerId);
  /*
   * "Received in" is a question about a BANK, so it is only put when the money went through one.
   *
   * It used to be drawn under every mode with a tooltip reading "leave blank for cash-in-hand" — a
   * picker that explained, in its own tooltip, that it did not apply. Worse than noise: the answer
   * was accepted and stored, and the cash-versus-bank split is read off precisely these two columns,
   * so one cash collection tagged to an account reads as banked money that no statement can match.
   */
  const mode = Form.useWatch('mode', form);
  const bankApplies = !!mode && mode !== 'CASH';
  /*
   * Proof follows the same line as the bank account, for the same reason: money that moved through a
   * bank leaves evidence, and cash counted at a counter does not. Demanding a file for cash produces a
   * photograph of a desk or a blocked collection, neither of which makes the record truer. Cash gets
   * its evidence at the hand-over instead, where a deposit slip is mandatory.
   */
  const proofRequired = bankApplies;

  useEffect(() => {
    if (open) {
      setRetailer(retailerId);
      setTarget(undefined);
      setManual(null);
      form.resetFields();
      form.setFieldsValue({ paymentDate: dayjs(), mode: 'UPI', retailerId });
    }
  }, [open, retailerId, form]);

  // Searched on the server; a pre-filled retailer outside the first page is fetched by id for its label.
  const retailerPick = useRetailerSearch({ key: 'retailers-for-payment', enabled: open, selected: retailer });

  // Active company bank accounts for the "received in" picker. Endpoint is gated by the payment perms,
  // so any user who can reach this modal can read it (returns [] if none configured).
  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
    enabled: open,
  });
  const bankOpts: any[] = Array.isArray(bankAccounts) ? bankAccounts : ((bankAccounts as any)?.data ?? []);

  /*
   * WHAT THIS DEALER OWES, BILL BY BILL — from the server, which counts pending approvals too and
   * knows which orders are payable at all. The old list came off the orders endpoint and could offer
   * an unapproved order, which has no proforma and therefore nothing to pay against.
   */
  const { data: duesRaw } = useQuery({
    queryKey: ['retailer-open-dues', retailer],
    queryFn: () => request<any>({ url: `/retailers/${retailer}/open-dues` }),
    enabled: open && !!retailer,
  });
  const dueOrders: any[] = (Array.isArray(duesRaw) ? duesRaw : ((duesRaw as any)?.data ?? []));
  const payableDues = dueOrders.filter((o) => o.payable);
  const unpayable = dueOrders.filter((o) => !o.payable);
  const blockedTotal = unpayable.reduce((n, o) => n + Number(o.due), 0);
  const [showBlocked, setShowBlocked] = useState(false);
  const totalDue = payableDues.reduce((n, o) => n + Number(o.due), 0);

  /*
   * HOW THE NOTES ARE SPLIT. A dealer hands over ₹800 when one bill is ₹200 and another ₹1,000 — the
   * money settles the oldest bill first and the rest goes to the next, which is what a ledger clerk
   * does and what the rep would say out loud. The rep can override any line; whatever the bills cannot
   * absorb is shown as on-account credit rather than being quietly forced onto a bill.
   */
  const amount = Number(Form.useWatch('amount', form) ?? 0);
  const [manual, setManual] = useState<Record<string, number> | null>(null);
  /*
   * WHAT THIS MONEY IS FOR — a choice, not an inference.
   *
   * "Across open bills" is the ordinary collection: a dealer hands over notes and the ledger settles
   * the oldest first. "One order only" is a different statement — the dealer named the order — and it
   * has to travel to the server as intent, because an order carrying an advance cash discount keeps
   * it only if the payments against THAT order cover its net. Spreading such a payment oldest-first
   * leaves the new order short and the discount is dropped at billing, which is precisely what the
   * rep did not agree to. So the two modes send two different contracts and the server decides.
   */
  const [target, setTarget] = useState<string | undefined>();
  const targetDue = payableDues.find((o: any) => String(o.orderId) === String(target));
  const targetCd = targetDue?.advanceCd ?? null;
  const plan = (() => {
    const rows: any[] = [];
    if (manual) {
      for (const o of payableDues) {
        const take = Math.min(Number(manual[String(o.orderId)] ?? 0), Number(o.due));
        if (take > 0.005) rows.push({ ...o, take: Number(take.toFixed(2)) });
      }
    } else {
      let left = amount;
      for (const o of payableDues) {
        if (left <= 0.005) break;
        const take = Math.min(Number(o.due), left);
        if (take <= 0.005) continue;
        rows.push({ ...o, take: Number(take.toFixed(2)) });
        left = Number((left - take).toFixed(2));
      }
    }
    const allocated = Number(rows.reduce((n, r) => n + r.take, 0).toFixed(2));
    return { rows, allocated, onAccount: Number(Math.max(0, amount - allocated).toFixed(2)) };
  })();

  const record = useConfirmedMutation({ title: "Record this payment?", content: "It posts to the retailer’s ledger once it is approved.", okText: "Yes, record" }, {
    mutationFn: (vals: any) => request<any>({
      // One collection, split across the bills it pays — the server posts one payment per bill so
      // every rupee keeps a document behind it.
      url: '/payments/collect', method: 'POST',
      data: {
        retailerId: vals.retailerId,
        paymentDate: vals.paymentDate.format('YYYY-MM-DD'),
        amount: vals.amount,
        mode: vals.mode,
        // ONE contract or the other, never both — the server refuses a request carrying two.
        ...(target
          ? { targetOrderId: String(target) }
          : { allocations: plan.rows.map((r: any) => ({ orderId: String(r.orderId), amount: r.take })) }),
        referenceNo: vals.referenceNo,
        utr: vals.utr,
        receivedBankAccountId: bankApplies ? (vals.receivedBankAccountId || undefined) : undefined,
        attachmentUrls: vals.attachmentUrls,
      },
    }),
    onSuccess: (r: any) => {
      const d = r?.data ?? r;
      const n = d.payments?.length ?? 0;
      message.success(
        n > 1
          ? `${inr2(d.collected)} collected — split across ${n} bills (${d.payments.map((p: any) => p.paymentNo).join(', ')})`
          : `Payment ${d.payments?.[0]?.paymentNo ?? ''} recorded`,
      );
      if (d.onAccount > 0) message.info(`${inr2(d.onAccount)} had no open bill — kept on account as retailer credit.`);
      qc.invalidateQueries({ queryKey: ['payments'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['retailer-ledger'] });
      qc.invalidateQueries({ queryKey: ['retailer-open-dues'] });
      qc.invalidateQueries({ queryKey: ['rep-cash-me'] });
      qc.invalidateQueries({ queryKey: ['rep-cash-summary'] });
      qc.invalidateQueries({ queryKey: ['payments-bank-summary'] });
      qc.invalidateQueries({ queryKey: ['bank-register'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} width={640} title="Record Payment">
      <Form form={form} layout="vertical" onFinish={(v) => record.mutate(v)} initialValues={{ paymentDate: dayjs(), mode: 'UPI', retailerId }}>
        {/* [h, v] gutter — the vertical rowGap keeps each row's label off the previous row's field. */}
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item name="retailerId" label="Retailer" rules={[{ required: true }]}>
              <Select
                {...CUSTOMER_SELECT_PROPS}
                filterOption={false} onSearch={retailerPick.onSearch}
                onChange={(v) => { setRetailer(v); setTarget(undefined); setManual(null); }}
                options={retailerPick.rows.map((r: any) => customerOption(r))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            {/* "Payable now", not "outstanding": bills without a proforma are owed but cannot take
                money, and one figure covering both would understate what the dealer owes. */}
            <Form.Item label="Payable now">
              <div className="flex min-h-9 items-center rounded-control border border-line bg-surface-2/50 px-3 text-sm">
                {!retailer ? (
                  <span className="text-subtle">Pick a retailer first</span>
                ) : payableDues.length ? (
                  <span className="min-w-0 truncate text-content">
                    <b className="tabular-nums">{inr2(totalDue)}</b>
                    <span className="text-subtle"> across {payableDues.length} bill{payableDues.length === 1 ? '' : 's'}</span>
                    {blockedTotal > 0.005 && <span className="text-subtle"> · {inr2(blockedTotal)} not billable yet</span>}
                  </span>
                ) : (
                  <span className="text-subtle">
                    Nothing payable — goes on account{blockedTotal > 0.005 ? ` (${inr2(blockedTotal)} not billable yet)` : ''}
                  </span>
                )}
              </div>
            </Form.Item>
          </Col>
          {/* One-up on a phone, two-up to md: a date field has a ~156px floor, so three 1/3 columns in a
              320px dialog laid it over Amount and Mode. */}
          <Col xs={24} sm={12} md={8}><Form.Item name="paymentDate" label="Date" rules={[{ required: true }]}><DatePicker className="w-full" /></Form.Item></Col>
          <Col xs={24} sm={12} md={8}><Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}><InputNumber className="w-full" min={1} step={100} /></Form.Item></Col>
          <Col xs={24} sm={12} md={8}><Form.Item name="mode" label="Mode" rules={[{ required: true }]}>
            {/* Choosing CASH drops the account along with the question. The form KEEPS the value of an
                item it has unmounted, so it has to be cleared here or it would still be submitted. */}
            <Select options={MODES.map((m) => ({ value: m }))}
              onChange={(v: any) => { if (v === 'CASH') form.setFieldsValue({ receivedBankAccountId: undefined }); }} />
          </Form.Item></Col>
          {/* WHAT THIS MONEY IS FOR — decided here, in front of the person handing the notes over,
              instead of being inferred afterwards. Oldest bill first; the rep overrides when the
              dealer was specific. A single ₹800 collection could previously only point at ONE bill. */}
          {!!retailer && (
            <Col span={24}>
              <Form.Item label="What this payment settles">
                {/*
                  * THE ONE CONTROL THAT REMOVES THE AMBIGUITY: did the dealer hand this over against
                  * their ACCOUNT, or for a NAMED ORDER? One button quietly doing both jobs is how an
                  * advance for a new order ended up paying off last month's bill.
                  *
                  * Deliberately NOT in the Form.Item's `label` slot: that renders a real <label>
                  * element, so anything interactive inside it inherits the label's words into its
                  * accessible name ("What this payment settles One order only") and a click on the
                  * heading toggles the first segment.
                  */}
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <Segmented
                    size="small"
                    value={target ? 'order' : 'account'}
                    onChange={(v: any) => {
                      setManual(null);
                      setTarget(v === 'order' ? (payableDues[0] ? String(payableDues[0].orderId) : undefined) : undefined);
                    }}
                    options={[
                      { value: 'account', label: 'Across open bills' },
                      { value: 'order', label: 'One order only', disabled: !payableDues.length },
                    ]}
                  />
                  <span className="type-caption text-subtle">
                    {target ? 'this order only' : 'oldest bill first'}
                  </span>
                </div>
                {target ? (
                  <div className="overflow-hidden rounded-card border border-line">
                    <div className="border-b border-line bg-surface-2/60 px-3 py-2">
                      <Select
                        className="w-full" showSearch optionFilterProp="title" optionLabelProp="title"
                        value={target} onChange={(v: any) => setTarget(v)}
                        options={payableDues.map((o: any) => ({
                          value: String(o.orderId),
                          title: `${o.orderNo} · ${inr2(o.due)} outstanding`,
                          label: (
                            <span className="flex min-w-0 items-center justify-between gap-3">
                              <span className="min-w-0">
                                <span className="block truncate text-content">{o.orderNo}</span>
                                <span className="block truncate type-caption text-subtle">
                                  {o.orderDate ? formatIstDate(o.orderDate) : ''}
                                  {o.advanceCd ? ' · advance discount offered' : ''}
                                </span>
                              </span>
                              <span className="shrink-0 text-sm tabular-nums text-muted">{inr2(o.due)}</span>
                            </span>
                          ),
                        }))}
                      />
                    </div>
                    <dl className="divide-y divide-line text-sm">
                      <div className="flex items-center justify-between gap-3 px-3 py-2">
                        <dt className="text-muted">Order payable</dt>
                        <dd className="tabular-nums text-content">{inr2(targetDue?.due ?? 0)}</dd>
                      </div>
                      {/* The figure a rep would otherwise have to work out for themselves, and the
                          reason this mode exists. While the discount is only ESTIMATED the order's
                          own "due" is still the LIST value; the sum that actually earns the discount
                          is the net, and collecting anything less quietly forfeits it at billing. */}
                      {targetCd && (
                        <>
                          <div className="flex items-center justify-between gap-3 px-3 py-2">
                            <dt className="text-muted">Advance cash discount</dt>
                            <dd className="tabular-nums text-success-700 dark:text-success-500">&minus; {inr2(targetCd.cdAmount)}</dd>
                          </div>
                          <div className="flex items-center justify-between gap-3 bg-surface-2/40 px-3 py-2">
                            <dt className="text-content">Net payable to keep the discount</dt>
                            <dd className="tabular-nums font-semibold text-content">{inr2(targetCd.netDue)}</dd>
                          </div>
                        </>
                      )}
                      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2">
                        <span className="type-caption text-subtle">
                          The whole payment is applied to <b className="text-content">{targetDue?.orderNo}</b> &mdash; no part of
                          it goes to an older bill.
                        </span>
                        {amount > Number(targetDue?.due ?? 0) + 0.005 && (
                          <span className="type-caption text-muted">
                            {inr2(amount - Number(targetDue?.due ?? 0))} kept <b className="text-content">on account</b> as credit
                          </span>
                        )}
                        {targetCd && amount > 0.005 && amount < targetCd.netDue - 0.005 && (
                          <span className="type-caption text-warning-600 dark:text-harvest-300">
                            {inr2(targetCd.netDue - amount)} short of the net &mdash; the discount will not lock yet
                          </span>
                        )}
                      </div>
                    </dl>
                  </div>
                ) : (
                <div className="overflow-hidden rounded-card border border-line">
                  {payableDues.length ? (
                    <>
                      <div className="grid grid-cols-[1fr_auto_116px] items-center gap-3 border-b border-line bg-surface-2/60 px-3 py-1.5 type-overline text-subtle">
                        <span>
                          Bill
                          {manual && (
                            <button type="button" className="ml-2 normal-case text-brand-700 hover:underline dark:text-brand-400"
                              onClick={() => setManual(null)}>reset to oldest first</button>
                          )}
                        </span>
                        <span className="text-right">Outstanding</span><span className="text-right">Paying</span>
                      </div>
                      {/* Capped: a dealer with a dozen open bills must not push the rest of the form
                          off the screen. */}
                      <ul className="max-h-52 divide-y divide-line overflow-y-auto">
                        {payableDues.map((o: any) => {
                          const take = plan.rows.find((r: any) => String(r.orderId) === String(o.orderId))?.take ?? 0;
                          const full = take > 0.005 && take >= Number(o.due) - 0.005;
                          const part = take > 0.005 && !full;
                          return (
                            <li key={o.orderId}
                              className={`grid grid-cols-[1fr_auto_116px] items-center gap-3 px-3 py-2 ${take > 0.005 ? '' : 'opacity-55'}`}>
                              <span className="min-w-0">
                                <span className="block truncate text-sm text-content">{o.orderNo}</span>
                                <span className="block truncate type-caption text-subtle">
                                  {o.orderDate ? formatIstDate(o.orderDate) : ''}
                                  {full && <span className="text-brand-700 dark:text-brand-400"> · settles it in full</span>}
                                  {part && <span className="text-warning-600 dark:text-harvest-300"> · part · {inr2(Number(o.due) - take)} still due</span>}
                                </span>
                              </span>
                              <span className="whitespace-nowrap text-right text-sm tabular-nums text-muted">{inr2(o.due)}</span>
                              {/* Width belongs on the WRAPPER — a className width lands on the inner
                                  input and the row collapses (ui/InputNumber's width split). */}
                              <InputNumber
                                style={{ width: 116 }} min={0} max={Number(o.due)} step={100} value={take}
                                onChange={(v) => setManual({
                                  ...(manual ?? Object.fromEntries(payableDues.map((d: any) => [
                                    String(d.orderId),
                                    plan.rows.find((r: any) => String(r.orderId) === String(d.orderId))?.take ?? 0,
                                  ]))),
                                  [String(o.orderId)]: Number(v ?? 0),
                                })}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  ) : (
                    <div className="px-3 py-3 text-sm text-subtle">
                      No open bill for this dealer — the whole amount is kept on account as credit.
                    </div>
                  )}

                  {/* Bills that cannot take money are summarised, not listed: the outstanding total
                      must not look smaller than it is, but six identical "not approved" lines buried
                      the part the rep actually fills in. */}
                  {unpayable.length > 0 && (
                    <div className="border-t border-line bg-surface-2/40 px-3 py-2 type-caption text-subtle">
                      <button type="button" className="text-left hover:text-content" onClick={() => setShowBlocked((v) => !v)}>
                        {unpayable.length} more bill{unpayable.length === 1 ? '' : 's'} ({inr2(blockedTotal)}) can&rsquo;t take a payment
                        yet — not approved, so no proforma {showBlocked ? '▴' : '▾'}
                      </button>
                      {showBlocked && (
                        <ul className="mt-1 space-y-0.5 pl-1">
                          {unpayable.map((o: any) => (
                            <li key={o.orderId} className="truncate">{o.orderNo} · {inr2(o.due)}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-line px-3 py-2 text-sm">
                    <span className="text-muted">
                      Allocated <b className="tabular-nums text-content">{inr2(plan.allocated)}</b>
                      <span className="text-subtle"> of {inr2(amount)} collected</span>
                    </span>
                    {plan.onAccount > 0.005 ? (
                      <span className="type-caption text-muted">
                        {inr2(plan.onAccount)} kept <b className="text-content">on account</b> as credit
                      </span>
                    ) : plan.allocated > amount + 0.005 ? (
                      <span className="type-caption text-danger-600 dark:text-danger-500">The split is more than the amount collected</span>
                    ) : (
                      <span className="type-caption text-subtle">nothing left over</span>
                    )}
                  </div>
                </div>
                )}
              </Form.Item>
            </Col>
          )}
          {bankApplies && (
            <Col span={24}>
              <Form.Item
                name="receivedBankAccountId"
                label="Received in (bank account)"
                rules={[{ required: true, message: 'Select the receiving bank account' }]}
                tooltip="Which of your company bank accounts this payment landed in."
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="searchLabel"
                  placeholder={bankOpts.length ? 'Select the receiving bank account' : 'No bank accounts configured — add under Masters → Company bank details'}
                  disabled={!bankOpts.length}
                  options={bankOpts.map(bankAcctOption)}
                />
              </Form.Item>
            </Col>
          )}
          <Col span={12}><Form.Item name="referenceNo" label="Reference No"><Input placeholder="Cheque / UPI ref" maxLength={40} /></Form.Item></Col>
          <Col span={12}><Form.Item name="utr" label="UTR / Bank Ref"><Input maxLength={40} /></Form.Item></Col>
          <Col span={24}>
            <Form.Item
              name="attachmentUrls"
              label={proofRequired ? 'Payment proof (receipts)' : 'Payment proof (optional)'}
              rules={proofRequired ? [{ required: true, type: 'array', min: 1, message: 'Upload at least one receipt / proof' }] : []}
              extra={proofRequired
                ? 'Required — receipts, cheque images, or transfer screenshots (images or PDF).'
                : 'Optional for cash taken by hand. The slip that matters is the one attached when this cash is handed over or banked.'}
            >
              <MultiFileUploadField category="receipt" buttonLabel="Upload receipts" />
            </Form.Item>
          </Col>
        </Row>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          Recorded by staff — posts to the ledger immediately (no approval step).
        </Typography.Paragraph>
        <Button variant="primary" type="submit" loading={record.isPending} block>Record payment</Button>
      </Form>
    </Modal>
  );
}
